import { types } from 'mobx-state-tree';
import type { Instance } from 'mobx-state-tree';
import Cookies from 'js-cookie';
import { includes } from 'lodash-es';

import { COOKIE_USER_LOCALE, COOKIE_DURATION_IN_DAYS, HEADER_TOOLS } from '@/constants';

import { supportedLocales, SupportedLocale, SupportedLocales } from '@i18n/supported-locales';

import { isDev, isJakhubForChina } from '@utils/build-utils';
import { localeToLanguage } from '@utils/utils';

import DisablableComponentsModel from './application-stores/models/disablable-components-model';
import CountriesRegionsStore from './application-stores/countries-regions-store';
import HeaderToolsStore from './application-stores/header-tools-store';
import SchoolStore from './application-stores/school-store';
import ThirdPartyServicesStore from './application-stores/third-party-services-store';

import { createApiAction } from '@/api/api-action-factory';
import { TypedStoreConfig } from './factories/store';

import {
    ALIVE_ENDPOINT,
    ENVIRONMENT_ID_ENDPOINT,
    CREATE_SCHOOL_ENDPOINT,
    CONTACT_AUDIOKINETIC_ENDPOINT,
    COMPONENT_ENABLER_STATIC_ENDPOINT,
} from '@/api/endpoints';
import { ApplicationLanguage, ApplicationLocale } from '@signals/locale';

const { SEARCH_BAR, SIGN_IN, SIGN_UP, CONTACT_FORM } = HEADER_TOOLS;

const MOBILE_MENU_CLOSERS = [SIGN_IN, SIGN_UP, SEARCH_BAR, CONTACT_FORM];

const BackendIsAliveModel = types.model('BackendIsAliveModel', {
    title: types.maybeNull(types.string),
    author: types.maybeNull(types.string),
    branch: types.maybeNull(types.string),
    date: types.maybeNull(types.string),
    subject: types.maybeNull(types.string),
});

const ApiIsAlive = types.model('ApiIsAlive', {
    version: types.optional(BackendIsAliveModel, {}),
    pricingGroup: types.maybeNull(types.string),
    loginBaseUrl: types.maybeNull(types.string),
    loginClientId: types.maybeNull(types.string),
});
type TypeApiIsAlive = Instance<typeof ApiIsAlive>;
const ApiIsAliveModel = types.snapshotProcessor(ApiIsAlive, {
    preProcessor(sn: TypeApiIsAlive) {
        const emptyVersion = { title: '', author: '', branch: '', date: '', subject: '' };
        return {
            ...sn,
            version: sn?.version ? emptyVersion : sn?.version,
        };
    },
});
type TypeApiIsAliveModel = Instance<typeof ApiIsAliveModel>;

const SchoolCreateModel = types.model('SchoolCreateModel', {
    school: types.model('school', { code: types.number }),
});

const ApplicationStore = TypedStoreConfig({
    modelName: 'ApplicationStore',
    properties: {
        i18nLoaded: false,
        serverDate: '',
        userLocale: types.optional(
            types.enumeration<SupportedLocale>(Object.values(supportedLocales)),
            supportedLocales[1]
        ),
        headerTools: HeaderToolsStore,
        headerToolId: '',
        backendAlive: types.maybeNull(ApiIsAliveModel),
        backendEnvironmentId: types.string,
        thirdPartyServices: ThirdPartyServicesStore,
        countriesRegionsStore: CountriesRegionsStore,
        SchoolStore: SchoolStore,
        schoolCreateResult: types.maybeNull(SchoolCreateModel),
        unwantedResponse: types.frozen({}),
        enabledComponents: DisablableComponentsModel,
    },
})
    .actions((self) => ({
        getEnabledComponent: createApiAction(self, {
            endpoint: COMPONENT_ENABLER_STATIC_ENDPOINT,
            target: 'enabledComponents',
        }),
        isBackendAlive: createApiAction(self, {
            endpoint: ALIVE_ENDPOINT,
            target: 'backendAlive',
            getServerDate: true,
        }),
        getBackendEnvironmentId: createApiAction(self, {
            endpoint: ENVIRONMENT_ID_ENDPOINT,
            target: 'backendEnvironmentId',
        }),
        sendContactForm: createApiAction(self, {
            endpoint: CONTACT_AUDIOKINETIC_ENDPOINT,
            target: 'unwantedResponse',
            onTrigger(self) {
                self.showToolInHeader('', '');
            },
        }),
        submitSchoolCreateForm: createApiAction(self, {
            endpoint: CREATE_SCHOOL_ENDPOINT,
            target: 'schoolCreateResult',
        }),
    }))
    .actions((self) => ({
        afterCreate() {
            self.getEnabledComponent();
        },
        clearSchoolCreateResult() {
            self.schoolCreateResult = null;
        },
        clearDump() {
            self.unwantedResponse = {};
        },
        closeMobileMenu() {
            self.headerTools.mobileMenuOpened = false;
        },
    }))
    .actions((self) => ({
        setUserLocale(newLocale: SupportedLocales) {
            self.userLocale = newLocale;
            self.closeMobileMenu();
            const {
                location: { hostname },
            } = window;
            const hostName = hostname.split('.').slice(-2).join('.');

            ApplicationLocale.value = newLocale;
            ApplicationLanguage.value = localeToLanguage(newLocale);

            Cookies.set(COOKIE_USER_LOCALE, localeToLanguage(newLocale), {
                domain: hostName,
                expires: COOKIE_DURATION_IN_DAYS,
            });
        },
        showToolInHeader(toolToShow: string, id = '') {
            const { openedHeaderToolId, openedHeaderTool } = self.headerTools;
            const newToolId = id === openedHeaderToolId ? '' : id;

            self.headerTools = {
                ...self.headerTools,
                openedHeaderToolId: !(openedHeaderTool === toolToShow && newToolId === '')
                    ? newToolId
                    : '',
                openedHeaderTool: !(openedHeaderTool === toolToShow && newToolId === '')
                    ? toolToShow
                    : '',
            };

            if (includes([...MOBILE_MENU_CLOSERS, ''], toolToShow)) {
                self.closeMobileMenu();
            }
        },
    }))
    .actions((self) => ({
        toggleMobileMenu() {
            const currentMobileMenuState = self.headerTools.mobileMenuOpened;
            self.showToolInHeader('');
            self.headerTools.mobileMenuOpened = !currentMobileMenuState;
        },
    }))
    .actions((self) => ({
        hideAllHeaderTools() {
            self.showToolInHeader('');
        },
    }))
    .views((self) => ({
        getUserLocale(): string {
            return self.userLocale || '';
        },
        get userLanguage(): string {
            const defaultUserLanguage = isJakhubForChina() ? 'zh' : supportedLocales[0];
            return self.userLocale.split('-')[0] || defaultUserLanguage;
        },
        applicationIsLoaded() {
            return self.i18nLoaded;
        },
        getLoginClientInfo() {
            const { loginBaseUrl, loginClientId } = { ...self.backendAlive };
            return { loginBaseUrl, loginClientId };
        },
    }));

export type TypeApplicationStore = Instance<typeof ApplicationStore>;

const ApplicationStoreInstance = ApplicationStore.create({
    headerTools: {},
    thirdPartyServices: {},
    backendEnvironmentId: '',
    countriesRegionsStore: {},
    SchoolStore: {},
    enabledComponents: {},
});

declare const window: Window &
    typeof globalThis & {
        ApplicationStore: typeof ApplicationStoreInstance;
    };
if (window && isDev()) {
    window.ApplicationStore = ApplicationStoreInstance;
}

export default ApplicationStoreInstance;
